'use client';

import { useState, useEffect } from 'react';
import { PublicLink } from '@/components/PublicLink';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AnimatedContainer } from '@/components/ui/animated-container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Loader2, Copy, CheckCircle, Clock, AlertTriangle, Lock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { maybeUtcDate } from '@/lib/time';
import { AppPageShell, PageHero, PageIntro, PageSection } from '@/components/site/page-shell';

// Define the API key type
interface ApiKey {
  id: string;
  name: string;
  key: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  kongConsumerId?: string | null;
  kongConsumerUsername?: string | null;
  kongCredentialId?: string | null;
  isManaged: boolean;
}

// Form schema for creating a new API key
const createApiKeySchema = z.object({
  name: z.string().min(1, { message: 'Название обязательно' }).max(100),
});

type CreateApiKeyData = z.infer<typeof createApiKeySchema>;

function formatRelativeToNow(value?: string | null) {
  const date = maybeUtcDate(value);
  if (!date) {
    return 'Никогда';
  }
  return `${formatDistanceToNow(date, { locale: ru })} назад`;
}

export default function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const { toast } = useToast();
  const [keyToCopy, setKeyToCopy] = useState<string | null>(null);

  const form = useForm<CreateApiKeyData>({
    resolver: zodResolver(createApiKeySchema),
    defaultValues: {
      name: '',
    },
  });

  // Fetch API keys
  useEffect(() => {
    const fetchApiKeys = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/user/api-keys');
        
        if (response.ok) {
          const data = await response.json();
          setApiKeys(data.apiKeys);
        } else {
          console.error('Failed to fetch API keys');
        }
      } catch (error) {
        console.error('Error fetching API keys:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApiKeys();
  }, []);

  // Create a new API key
  const onSubmit = async (data: CreateApiKeyData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/user/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific case for duplicate API key name (409 Conflict)
        if (response.status === 409) {
          form.setError('name', { 
            type: 'manual', 
            message: errorData.error || 'API-ключ с таким названием уже существует'
          });
          throw new Error(errorData.error || 'API-ключ с таким названием уже существует');
        }
        
        throw new Error(errorData.error || 'Не удалось создать API-ключ');
      }

      const result = await response.json();
      
      // Store the new API key to display to the user (only shown once)
      setNewApiKey(result.apiKey.key);
      
      // Add the new API key to the list
      setApiKeys(prevKeys => [result.apiKey, ...prevKeys]);
      
      // Reset the form
      form.reset();
      
      toast({
        title: 'API-ключ создан',
        description: 'Новый API-ключ успешно создан',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Не удалось создать ключ',
        description: error.message || 'Не удалось создать API-ключ',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Revoke an API key
  const revokeApiKey = async (keyId: string) => {
    try {
      const response = await fetch(`/api/user/api-keys/${keyId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось отозвать API-ключ');
      }

      // Update the local state
      setApiKeys(prevKeys => 
        prevKeys.map(key => 
          key.id === keyId ? { ...key, isActive: false } : key
        )
      );
      
      toast({
        title: 'API-ключ отозван',
        description: 'API-ключ успешно отозван',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Не удалось отозвать ключ',
        description: error.message || 'Не удалось отозвать API-ключ',
      });
    }
  };

  // Copy API key to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setKeyToCopy(text);
    
    toast({
      title: 'Скопировано',
      description: 'API-ключ скопирован в буфер обмена',
    });
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setKeyToCopy(null);
    }, 2000);
  };

  return (
    <ProtectedRoute>
      <AppPageShell
        hero={
          <PageHero>
            <PageIntro
              eyebrow="Доступ для разработчиков"
              title="API-ключи"
              description="Создавайте, отзывайте и копируйте учетные данные для интеграций S-SHOT."
              actions={
                <div className="flex flex-wrap gap-3">
                  <PublicLink href="/api-documentation">
                    <Button variant="outline">Документация API</Button>
                  </PublicLink>
                  <PublicLink href="/settings">
                    <Button variant="outline">Настройки аккаунта</Button>
                  </PublicLink>
                </div>
              }
            />
          </PageHero>
        }
      >
        <PageSection>
          <AnimatedContainer animation="fadeIn" className="space-y-8">

            {/* Create new API key */}
            <Card>
              <CardHeader>
                <CardTitle>Создать API-ключ</CardTitle>
                <CardDescription>
                  Создайте новый API-ключ для доступа к API S-SHOT
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4 rounded-md border border-blue-100 bg-[color:rgba(8,145,178,0.1)] p-3 text-sm text-blue-700">
                    API-ключи, созданные здесь, сразу публикуются в Kong. Используйте значение ниже с{' '}
                    <code className="mx-1 rounded bg-blue-100 px-1">Authorization: Bearer ws_...</code>{' '}
                    при вызове эндпоинтов вроде{' '}
                    <code className="mx-1 rounded bg-blue-100 px-1">/v1.0/extract-async</code>.
                </div>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Название ключа</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Например: Продакшн, Тестирование, Разработка"
                            />
                          </FormControl>
                          <FormDescription>
                            Дайте ключу понятное название, чтобы различать его назначение
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Создание...
                        </>
                      ) : (
                        'Создать API-ключ'
                      )}
                    </Button>
                  </form>
                </Form>

                {/* Display newly created API key */}
                {newApiKey && (
                  <div className="mt-6 rounded-md border border-yellow-200 bg-yellow-50 p-4">
                    <h3 className="mb-2 flex items-center text-sm font-medium text-yellow-800">
                      <AlertTriangle className="mr-1 h-4 w-4" />
                      Сохраните API-ключ сейчас
                    </h3>
                    <p className="mb-2 text-xs text-yellow-700">
                      Полный API-ключ показывается только один раз. Скопируйте его и сохраните в безопасном месте.
                    </p>
                    <div className="flex items-center justify-between rounded border border-yellow-300 bg-white p-2">
                      <code className="text-sm font-mono break-all">{newApiKey}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(newApiKey)}
                        className="ml-2"
                      >
                        {keyToCopy === newApiKey ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-2 h-auto p-0 text-yellow-800"
                      onClick={() => setNewApiKey(null)}
                    >
                      Скрыть
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* API keys list */}
            <Card>
                <CardHeader>
                  <CardTitle>Ваши API-ключи</CardTitle>
                  <CardDescription>
                    Управляйте существующими API-ключами
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 rounded-md border border-blue-100 bg-[color:rgba(8,145,178,0.1)] px-3 py-2 text-xs text-blue-700 flex items-center gap-2">
                    <Lock className="h-3 w-3" />
                    <span>Ключи с пометкой «Системный» используются самим веб-интерфейсом и не могут быть удалены.</span>
                  </div>
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-[var(--site-ink-subtle)]" />
                    </div>
                  ) : apiKeys.length === 0 ? (
                    <div className="text-center py-8 text-[var(--site-ink-muted)]">
                      <p>У вас пока нет API-ключей</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {apiKeys.map((apiKey) => (
                        <div 
                          key={apiKey.id}
                          className={`p-4 rounded-lg border ${
                            apiKey.isActive 
                              ? 'border-[color:var(--site-border-soft)] bg-white' 
                              : 'border-[color:var(--site-border-soft)] bg-[var(--site-surface-inset)]'
                          }`}
                        >
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{apiKey.name}</h3>
                                {apiKey.isActive ? (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">
                                    Активен
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                                    Отозван
                                  </span>
                                )}
                                {apiKey.isManaged && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 inline-flex items-center gap-1">
                                    <Lock className="h-3 w-3" />
                                    Системный
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center mt-1 text-sm text-[var(--site-ink-muted)] gap-2">
                                {apiKey.isManaged ? (
                                  <span className="font-mono text-[var(--site-ink-subtle)]">Скрыт (управляется веб-интерфейсом)</span>
                                ) : (
                                  <>
                                    <span className="font-mono">{apiKey.key.substring(0, 10)}...{apiKey.key.substring(apiKey.key.length - 5)}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyToClipboard(apiKey.key)}
                                      className="h-7 px-2"
                                    >
                                      {keyToCopy === apiKey.key ? (
                                        <CheckCircle className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <Copy className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </>
                                )}
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 text-xs text-[var(--site-ink-muted)]">
                                <span className="flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Создан {formatRelativeToNow(apiKey.createdAt)}
                                </span>
                                {apiKey.lastUsedAt && (
                                  <span className="flex items-center sm:ml-2">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Последнее использование {formatRelativeToNow(apiKey.lastUsedAt)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-2 md:mt-0">
                              {apiKey.isManaged ? (
                                <div className="text-xs text-[var(--site-ink-muted)] flex items-center gap-1">
                                  <Lock className="h-3 w-3" />
                                  <span>Используется веб-интерфейсом</span>
                                </div>
                              ) : (
                                apiKey.isActive && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="destructive" size="sm">
                                        Отозвать
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Это действие нельзя отменить. После отзыва API-ключ сразу станет недействительным, и все приложения, которые его используют, перестанут работать.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Отмена</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => revokeApiKey(apiKey.id)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Отозвать ключ
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
            </Card>

            {/* API usage instructions */}
            <Card>
                <CardHeader>
                  <CardTitle>Как использовать API-ключи</CardTitle>
                  <CardDescription>
                    Узнайте, как аутентифицировать API-запросы
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    Для аутентификации API-запросов передавайте API-ключ в заголовке <code className="bg-[color:rgba(23,38,59,0.06)] px-1 py-0.5 rounded">Authorization</code> через Bearer-аутентификацию:
                  </p>
                  
                  <div className="bg-[color:rgba(23,38,59,0.06)] p-3 rounded-md">
                    <code className="text-sm font-mono break-all">
                      Authorization: Bearer ws_your_api_key
                    </code>
                  </div>
                  
                  <p className="text-sm text-[var(--site-ink)]">
                    Подробности и примеры доступны на странице{' '}
                    <PublicLink href="/api-documentation" className="text-blue-600 hover:underline">
                      документации API
                    </PublicLink>{' '}
                    .
                  </p>
                </CardContent>
            </Card>
          </AnimatedContainer>
        </PageSection>
      </AppPageShell>
    </ProtectedRoute>
  );
}
